// libraries
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Stack, Typography, Alert, Tooltip } from '@mui/material'
import { MoreVert as MoreVertIcon, Lock as LockIcon } from '@mui/icons-material'
import Dinero from 'dinero.js'
import printJS from 'print-js'

// state
import { showFormModal } from 'state/ui'
import {
  clearActiveDialogState,
  clearNewCustomerVehicle,
  setPurchaseOrderInfo,
  clearSubmittedFromDispatchBlade,
} from 'state/inboundTicket'
import { clearCustomer, setCustomer } from 'state/customer'

// queries
import {
  useCreateInboundTicketMutation,
  useGetInboundTicketQuery,
  useUpdateInboundTicketMutation,
  useVoidInboundTicketMutation,
  useGetInboundTicketNotesQuery,
  useUpdateInboundTicketNotesMutation,
  useDeleteInboundTicketNoteMutation,
  useCreateInboundTicketNoteMutation,
} from 'services/inboundTickets'
import { useGetGsUserQuery } from 'services/gsUsers'
import { useGetBillQuery } from 'services/bills'
import { useGetCustomerQuery, useGetCustomersQuery } from 'services/customers'
import { useGetPurchaseOrderQuery } from 'services/purchaseOrders'
import { useGetVehicleQuery, useUpdateVehicleMutation } from 'services/vehicles'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { useGetFpSettingsQuery } from 'services/settings'

// specific components
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import InboundTicketScaleContainer from 'components/containers/inbound-tickets/InboundTicketScaleContainer'
import InboundTicketIndustrialContainer from 'components/containers/inbound-tickets/InboundTicketIndustrialContainer'

// generic components
import AlertSnackbar from 'components/generic/snackbars'
import PrintDialog from 'components/generic/printable/PrintDialog'
import SelectButton from 'components/generic/buttons/SelectButton'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'
import EmailModal from 'components/generic/modals/dialog-modal/EmailModal'

// helpers and utils
import { groupBy } from 'lodash'
import {
  PRINT_ACTION,
  renderActiveDialog,
  updateTicketActionsAndStatus,
} from 'components/containers/inbound-tickets/helpers/ticketActionsAndStatus'
import {
  formatVehicleData,
  generateCheckNumberText,
  generatePaymentMethodText,
  getCustomerId,
  initialLoadInformation,
  isTransferFromInboundTicketData,
} from 'components/containers/inbound-tickets/utils'
import { getFormOptions } from 'utils/validation/inboundTicket'
import {
  BILL_STATUSES,
  CHURN_ZERO_EVENTS as cz,
  IBT_VIEW_OPTIONS,
  TICKET_STATUSES,
  TICKET_TYPES,
  PDF_TYPE,
} from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import FORM_MODALS from 'utils/ui/modals'
import { formatScrapVehicles } from 'utils/data-manipulation/common'
import { dispatchSnackbar, locationIsNotEmpty, checkIfDateIsLocked, generateLockTooltip } from 'utils/helpers'
import { request } from 'customRequest'
import useGetSignatureAndFingerprint from 'utils/custom-hooks/useGetSignatureAndFingerprint'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { clearSupplementalPriceSheetIds, setSupplementalPriceSheetIds } from 'state/materials'
import useRoundingDelta from '../../../hooks/useRoundingDelta'
import useQzTray from 'utils/custom-hooks/useQzTray'
import useDoNotBuyCheck from './helpers/useDoNotBuyCheck'

const InboundTicketContainer = () => {
  // Keep track of the timer used to pool the server for the latest data
  const conflictTimerId = useRef(null)
  const pooledIbtId = useRef(null)
  const ticketHash = useRef(null)

  const { isClearingReduxFormState } = useSelector(state => state.ui || {})

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [viewMode, setViewMode] = useState(
    window.localStorage.getItem('inboundTicketViewMode') || IBT_VIEW_OPTIONS.INDUSTRIAL,
  )
  const [errorMessage, setErrorMessage] = useState('')
  const { customer: supplier } = useSelector(state => state.customer)
  const { purchaseOrderId, scrapVehicles, scrapVehiclesWereEdited } = useSelector(
    state => state.inboundTicket.transportationInformation,
  )
  const { activeDialogState, submittedFromDispatchBlade } = useSelector(state => state.inboundTicket)
  const [activeDialog, setActiveDialog] = useState(null)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [hasEditedMaterials, setHasEditedMaterials] = useState(false)
  const [paymentsData, setPaymentsData] = useState([])

  const [loadInformation, setLoadInformation] = useState(initialLoadInformation)
  const supplierRef = useRef({})
  const customerDataRef = useRef({})
  const loadInformationRef = useRef(initialLoadInformation)
  const { qzPrintResponsive } = useQzTray()

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [currentTotal, setCurrentTotal] = useState(0)

  const handleOpenEmailModal = () => setIsEmailModalOpen(true)
  const handleCloseEmailModal = () => setIsEmailModalOpen(false)

  const {
    showMergeTickets,
    showCheckInventoryTransfer,
    enableIbtAutosave,
    enableTruckInYardIBTStatusFeatureFlag,
    enableMaterialPaymentHolds,
    enableFePdfs,
    useMinimalObt,
    enablePdfEmails,
    enableTransferTicketsConsumerWeightsFF,
    enableLockAccountingV2,
    enableNegativeIbtToInvoice,
  } = useSelector(state => state.featureFlags?.variables || {})
  const mouseInactiveRef = useRef({ inactive: true, timeout: null })
  const scrollInactiveRef = useRef({ inactive: true, timeout: null })
  const keyboardInactiveRef = useRef({ inactive: true, timeout: null })
  const [userIsInactive, setUserIsInactive] = useState(false)
  const [isFetchingMaterialPrices, setIsFetchingMaterialPrices] = useState(false)
  const [isFetchingPdf, setIsFetchingPdf] = useState(false)
  const [purchaseOrderLocationId, setPurchaseOrderLocationId] = useState(null)

  useEffect(() => {
    // this useEffect is necessary because when the IBT form is dirty, the customer does not get cleared properly by clearReduxFormState
    if (isClearingReduxFormState) {
      dispatch(clearCustomer())
    }
  }, [isClearingReduxFormState])

  useEffect(() => {
    dispatch(clearSubmittedFromDispatchBlade())

    const callbackFunc = ref => {
      setUserIsInactive(false)
      const eventRef = ref
      clearTimeout(eventRef.current.timeout)
      eventRef.current = {
        inactive: false,
        timeout: setTimeout(() => {
          eventRef.current.inactive = true
          if (
            mouseInactiveRef.current.inactive === true &&
            scrollInactiveRef.current.inactive === true &&
            keyboardInactiveRef.current.inactive === true
          ) {
            setUserIsInactive(true)
          }
        }, 10000),
      }
    }

    const mouseCallback = () => callbackFunc(mouseInactiveRef)
    const wheelCallback = () => callbackFunc(scrollInactiveRef)
    const keydownCallback = () => callbackFunc(keyboardInactiveRef)

    window.addEventListener('mousemove', mouseCallback)
    window.addEventListener('wheel', wheelCallback)
    window.addEventListener('keydown', keydownCallback)

    return () => {
      clearTimeout(mouseInactiveRef.current.timeout)
      clearTimeout(scrollInactiveRef.current.timeout)
      clearTimeout(keyboardInactiveRef.current.timeout)
      window.removeEventListener('mousemove', mouseCallback)
      window.removeEventListener('wheel', wheelCallback)
      window.removeEventListener('keydown', keydownCallback)
    }
  }, [])

  const methods = useForm(getFormOptions())
  const { control, setValue, clearErrors, formState, watch, setError, getValues } = methods
  const { errors, dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length || scrapVehiclesWereEdited
  const isCustomerInfoDirty = Object.keys(dirtyFields).some(key => ['customer', 'transportationInfo'].includes(key))
  const selectedCustomer = useWatch({
    control,
    name: 'customer.name',
    defaultValue: {
      id: '',
      customerCommonIdentifierString: '',
    },
  })
  const selectedCarrier = watch('transportationInfo.carrierLookup')

  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const { id: inboundTicketId } = useParams()
  const isUpdate = !!inboundTicketId
  const { settings } = useSelector(state => state.settings)
  const isAutoSaveSettingEnabled = settings?.purchaseSettings?.inboundTickets?.enableAutosave
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const isAutoSaveAndCloseAfterPrintingEnabled =
    settings?.purchaseSettings?.inboundTickets?.autoSaveAndCloseAfterPrintingReceipt
  const isAutoSaveAndCloseAfterPrintingScaleTicketEnabled =
    settings?.purchaseSettings?.inboundTickets?.autoSaveAndCloseAfterPrintingScaleReceipt

  const {
    data: inboundTicketData,
    error: inboundTicketError,
    isFetching: isFetchingInboundTicketData,
  } = useGetInboundTicketQuery({ ticketId: inboundTicketId }, { skip: !isUpdate })

  // redirect back to list view if IBT is not found (likely attempting to view a ticket for a different yard)
  useEffect(() => {
    if (inboundTicketError && inboundTicketError.status === 404) {
      navigate('/app/purchases/ticket-library')
    }
  }, [inboundTicketError, navigate])

  const isTransfer = isTransferFromInboundTicketData(inboundTicketData)

  const { data: ticketCreator, isFetching: isFetchingTicketCreatorData } = useGetGsUserQuery(
    { userId: inboundTicketData?.createdById },
    { skip: !inboundTicketData },
  )

  const { signatureImageData, fingerprintImageData, imagesAreReady } = useGetSignatureAndFingerprint({
    signatureImageFileName: paymentsData?.find(payment => !!payment?.signatureImageFileName)?.signatureImageFileName,
    fingerprintImageFileName: paymentsData?.find(payment => !!payment?.fingerPrintImageFileName)
      ?.fingerPrintImageFileName,
  })

  // Notes data currently stored on the backend
  const { data: inboundTicketNotes, isLoading: isLoadingInboundTicketNotes } = useGetInboundTicketNotesQuery(
    {
      inboundTicketId,
    },
    { skip: !inboundTicketId },
  )

  // Locally updated notes data
  const [localNotes, setLocalNotes] = useState(null)
  const localNotesRef = useRef(localNotes, null)

  // Keep local notes in sync with inboundTicketNotes
  useEffect(() => {
    if (inboundTicketNotes) {
      setLocalNotes(inboundTicketNotes)
    }
  }, [inboundTicketNotes])

  const [createInboundTicketNote] = useCreateInboundTicketNoteMutation()
  const [updateInboundTicketNotes] = useUpdateInboundTicketNotesMutation()
  const [deleteInboundTicketNote] = useDeleteInboundTicketNoteMutation()

  // Whenever local notes change, update the notes on the BE
  // This will make RTK re-fetch the notes, and update the UI
  useEffect(() => {
    // Skip until notes are actually initialized
    if (localNotes === null) {
      return
    }
    if (inboundTicketId && !isLoadingInboundTicketNotes && !isFetchingInboundTicketData) {
      // Update notes on the BE
      const originalData = inboundTicketNotes ?? []
      const newNotes = localNotes.filter(note => !originalData.some(originalNote => originalNote.id === note.id))
      const deletedNotes = originalData.filter(originalNote => !localNotes.some(note => note.id === originalNote.id))

      // Updated notes are in both original and new notes, but either isInternal or note is different
      const updatedNotes = localNotes.filter(note =>
        originalData.some(
          originalNote =>
            originalNote.id === note.id &&
            (originalNote.isInternal !== note.isInternal || originalNote.note !== note.note),
        ),
      )

      for (const note of newNotes) {
        createInboundTicketNote({
          inboundTicketId: inboundTicketId,
          body: {
            note: note.name,
            isInternal: note.isInternal,
          },
        })
      }

      for (const deletedNote of deletedNotes) {
        deleteInboundTicketNote({
          inboundTicketId: inboundTicketId,
          ibtNoteId: deletedNote.id,
        })
      }

      if (updatedNotes.length > 0) {
        const notesUpdateBody = updatedNotes.map(note => {
          return {
            id: note.id,
            note: note.name,
            isInternal: note.isInternal,
          }
        })
        updateInboundTicketNotes({
          inboundTicketId: inboundTicketId,
          body: notesUpdateBody,
        })
      }
    }
  }, [localNotes])

  const { data: billData, isLoading: isLoadingBillData } = useGetBillQuery(
    { billId: inboundTicketData?.billId },
    {
      refetchOnMountOrArgChange: true,
      skip: !inboundTicketData?.billId,
    },
  )

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId: selectedCustomer?.id },
    { refetchOnMountOrArgChange: true, skip: !selectedCustomer?.id },
  )
  const { data: carrierData, isLoading: isLoadingCarrierData } = useGetCustomerQuery(
    { customerId: selectedCarrier?.id || inboundTicketData?.carrierId },
    {
      refetchOnMountOrArgChange: true,
      skip: !selectedCarrier?.id && !inboundTicketData?.carrierId,
    },
  )

  const { data: customersData } = useGetCustomersQuery({
    filter: JSON.stringify({ search: 'Default Retail' }),
  })

  const { data: vehicleData, isLoading: isLoadingVehicleData } = useGetVehicleQuery(
    { vehicleId: inboundTicketData?.vehicleId },
    { refetchOnMountOrArgChange: true, skip: !inboundTicketData?.vehicleId },
  )
  const filteredVehicleData = useMemo(() => {
    if (!vehicleData || !selectedCustomer?.id) return null
    if (vehicleData.customerId !== selectedCustomer.id) return null
    return vehicleData
  }, [vehicleData, selectedCustomer?.id])

  const canUseDefaultCustomer = settings?.purchaseSettings?.inboundTickets?.useDefaultRetailCustomer
  const defaultRetailCustomer = customersData?.Items?.find(
    cust => cust.customerCommonIdentifierString === 'Default Retail',
  )

  const { data: purchaseOrderData, isLoading: isLoadingPurchaseOrderData } = useGetPurchaseOrderQuery(
    { purchaseOrderId },
    {
      refetchOnMountOrArgChange: true,
      skip: !purchaseOrderId,
    },
  )

  // prefetch initial material dropdown results (GREEN-8185)
  useGetMaterialTypesQuery({
    filter: JSON.stringify({
      search: '',
      priceSheetId: purchaseOrderData?.priceSheetId || '',
    }),
    pageSize: 50,
  })

  const [createInboundTicket, { isLoading: isLoadingCreateInboundTicket }] = useCreateInboundTicketMutation()
  const [updateInboundTicket, { isLoading: isLoadingUpdateInboundTicket }] = useUpdateInboundTicketMutation()
  const [voidInboundTicket, { isLoading: isLoadingVoidInboundTicket }] = useVoidInboundTicketMutation()
  const [updateVehicle, { isLoading: isLoadingUpdateVehicle }] = useUpdateVehicleMutation()
  const { checkDoNotBuyViolation, isCheckingBuyEligibility } = useDoNotBuyCheck()

  const { data: feesAndPromotionsData, isLoading: isLoadingFeesAndPromotionsData } = useGetFpSettingsQuery({})

  const isLoading =
    isFetchingInboundTicketData ||
    isLoadingBillData ||
    isLoadingCustomerData ||
    isLoadingCarrierData ||
    isLoadingVehicleData ||
    isLoadingPurchaseOrderData ||
    isLoadingCreateInboundTicket ||
    isLoadingUpdateInboundTicket ||
    isLoadingVoidInboundTicket ||
    isLoadingUpdateVehicle ||
    isFetchingTicketCreatorData ||
    isFetchingMaterialPrices ||
    isFetchingPdf ||
    isLoadingFeesAndPromotionsData ||
    isCheckingBuyEligibility

  const poolIbtData = async () => {
    const freshIbtData = await request(`/core-api/purchases/inbound-tickets/${inboundTicketData.id}`)

    // Use the form hash to compare with the fresh data hash, as the RTK query will get updated when errors happen
    const formHash = ticketHash?.current?.value ?? ''

    if (formHash !== freshIbtData.hash) {
      setHasDataConflict(true)
    } else {
      setHasDataConflict(false)
    }
  }

  // Set when BE data is different from FE data
  const [hasDataConflict, setHasDataConflict] = useState(false)
  useEffect(() => {
    if (isLoading) {
      return
    }

    // Whenever the IBT changes, we need to cancel the timer
    // Note it's ok to pass a null to clearTimeout. It will be ignored.
    clearTimeout(conflictTimerId.current)
    conflictTimerId.current = null
    pooledIbtId.current = null

    // When there's no data, there's no conflict, so we can return early
    if (!inboundTicketData) {
      return
    }

    if (ticketHash.current && ticketHash.current.value === '') {
      // Loading the ticket, so we set the hash. After the initial load,
      // we don't want to set it again, unless data was submitted and
      // accepted by the server. This is done on the success handlers of
      // the industrial and scale view containers.
      ticketHash.current.value = inboundTicketData.hash
    }

    // If the Ibt data changes, we need to create a new timer
    pooledIbtId.current = inboundTicketData.id
    conflictTimerId.current = setInterval(() => poolIbtData(), 10000)

    return () => {
      // Clear the timer when the component unmounts
      clearInterval(conflictTimerId.current)
    }
  }, [inboundTicketData, isLoading])

  useGlobalLoader(isLoading)

  const handlePrint = async (pdfUrl, docTitle) => {
    setIsFetchingPdf(true)

    try {
      const response = await request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      const ticketPrinter = localStorage.getItem('ticketPrinter')

      // If no printer configured, use printJS directly
      if (!ticketPrinter || ticketPrinter === 'none') {
        printJS({
          printable: response,
          type: 'pdf',
          base64: true,
          documentTitle: docTitle,
        })
      } else {
        // Try QZ print first, fall back to printJS if it fails
        await qzPrintResponsive(response, ticketPrinter).catch(() => {
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: docTitle,
          })
        })
      }

      // Handle auto-save and navigation if enabled
      if (isAutoSaveAndCloseAfterPrintingEnabled) {
        navigate('/app/purchases/ticket-library')
      }
    } catch (error) {
      dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
    } finally {
      setIsFetchingPdf(false)
    }
  }

  const billTaxRate = useMemo(
    () =>
      parseFloat(
        !billData || billData?.status?.toUpperCase() === BILL_STATUSES.DUE.id
          ? customerData?.taxRate
          : billData?.taxRate,
      ) || 0,
    [billData, customerData],
  )

  const { pricePrepend } = useGetPricePrepend({
    customerData,
    selectedCustomer,
  })
  const { pricePrepend: carrierPricePrepend } = useGetPricePrepend({
    customerData: carrierData,
    selectedCustomer: selectedCarrier,
  })

  const freightBillIsPaid = useMemo(() => {
    const freightBillStatus = inboundTicketData?.freightCost?.freightBill?.status
    return [BILL_STATUSES.PAID.id, BILL_STATUSES.PARTIAL.id].indexOf(freightBillStatus) > -1
  }, [inboundTicketData])

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!inboundTicketData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return (
      new Date(inboundTicketData.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
    )
  }, [lockAccountingPeriod, inboundTicketData, enableLockAccountingV2])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!inboundTicketData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, inboundTicketData.effectiveDate)
  }, [lockAccountingPeriod, inboundTicketData, enableLockAccountingV2])

  useEffect(() => {
    if (activeDialogState && inboundTicketData?.id && !isFetchingInboundTicketData) {
      setActiveDialog({
        ...activeDialogState,
        dialogTitle: `Scale Ticket #${inboundTicketData?.ticketId} | ${
          TICKET_STATUSES[inboundTicketData?.status?.value?.toString()]?.name
        }`,
      })
      dispatch(clearActiveDialogState())
    }
  }, [activeDialogState, inboundTicketData, isFetchingInboundTicketData])

  const fetchPaymentsData = async paymentIds => {
    await Promise.all(paymentIds.map(async paymentId => request(`/core-api/purchases/payments/${paymentId}`)))
      .then(results => {
        const filteredResults = results.filter(payment => payment.status !== 'VOID')
        setPaymentsData(filteredResults)
      })
      .catch(error => {
        const message = error?.data?.error || 'An error occurred loading the payments'
        dispatchSnackbar(dispatch, 'error', message)
      })
  }

  useEffect(() => {
    if (billData?.paymentIds?.length > 0) {
      fetchPaymentsData(billData.paymentIds)
    } else {
      setPaymentsData([])
    }
  }, [billData])

  const onUpdateViewMode = mode => {
    setViewMode(mode)
    window.localStorage.setItem('inboundTicketViewMode', mode)
  }

  const onScrapVehicleEdit = vehicleVin => {
    const scrapVehicleToBeUpdated = scrapVehicles.find(vehicle => vehicle.vin === vehicleVin)

    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.ADD_VEHICLE,
        formModalProps: {
          inboundTicketId,
          tab: 'scrap-vehicle',
          vehicleData: scrapVehicleToBeUpdated,
          inboundTicketData,
          showOnlyScrapVehicleTab: true,
        },
      }),
    )
  }

  const canMergeTickets = () =>
    isUpdate &&
    !hasDataConflict &&
    (inboundTicketData?.status?.value === TICKET_STATUSES.INCOMPLETE.id ||
      inboundTicketData?.status?.value === TICKET_STATUSES.SCALE_COMPLETE.id ||
      inboundTicketData?.status?.value === TICKET_STATUSES.PRICE_COMPLETE.id ||
      inboundTicketData?.status?.value === TICKET_STATUSES.APPROVED.id ||
      inboundTicketData?.status?.value === TICKET_STATUSES.TRUCK_IN_YARD.id) &&
    (enableTransferTicketsConsumerWeightsFF ? !isTransfer : true)

  const getViewActionsOptions = () => {
    const actions =
      viewMode === IBT_VIEW_OPTIONS.INDUSTRIAL
        ? [
            {
              id: 'scale-view-action',
              title: 'Switch to Scale View',
              action: 'scale-view-action',
              onClick: () => {
                onUpdateViewMode(IBT_VIEW_OPTIONS.SCALE)
              },
            },
          ]
        : [
            {
              id: 'scale-view-action',
              title: 'Switch to Industrial View',
              action: 'scale-view-action',
              onClick: () => {
                onUpdateViewMode(IBT_VIEW_OPTIONS.INDUSTRIAL)
              },
            },
          ]
    if (showCheckInventoryTransfer) {
      actions.push({
        id: 'inventory-transfer-ticket',
        title: 'Inventory Transfer Ticket',
        action: 'inventory-transfer-ticket',
        disabled: isUpdate,
        onClick: () => {
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.SELECT_AVAILABLE_TRANSFER,
            }),
          )
        },
      })
    }
    if (showMergeTickets) {
      actions.push({
        id: 'merge-tickets',
        title: 'Merge Tickets',
        action: 'merge-tickets',
        disabled: !canMergeTickets(),
        onClick: () => {
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.MERGE_TICKETS,
              formModalProps: {
                ticketId: inboundTicketData?.id,
                billId: inboundTicketData?.billId,
                ticketNumber: inboundTicketData?.ticketId,
                customerId: getCustomerId(null, inboundTicketData, null, null),
              },
            }),
          )
        },
      })
    }
    return actions
  }

  const setContainerTitle = useCallback(() => {
    const containerTitle = inboundTicketData ? `Inbound Ticket #${inboundTicketData?.ticketId}` : 'New Inbound Ticket'

    contentContext.setContainerTitle(
      <Stack direction="row" alignItems="center" spacing={1}>
        {isInLockedAccountingPeriodV2 && (
          <Tooltip title={generateLockTooltip('inbound ticket')} placement="top">
            <LockIcon color="info" />
          </Tooltip>
        )}
        <Typography variant="h5">{containerTitle}</Typography>
        {!useMinimalObt && (
          <SelectButton
            text=""
            variant="text"
            color="inherit"
            endIcon={<MoreVertIcon />}
            disabled={isDirty || hasEditedMaterials}
            options={getViewActionsOptions()}
            onClick={option => option.onClick()}
          />
        )}
      </Stack>,
    )
  }, [viewMode, inboundTicketData, isUpdate, isDirty, hasEditedMaterials])

  useEffect(() => setContainerTitle(), [viewMode, inboundTicketData, isUpdate, isDirty, hasEditedMaterials])

  const isVoided = useCallback(() => inboundTicketData?.status?.value === TICKET_STATUSES.VOID.id, [inboundTicketData])

  const ibtIsLinkedToBill = () => enableMaterialPaymentHolds && billData?.isMultiTicket
  const ibtIsInvoiced = () => billData?.status === BILL_STATUSES.INVOICED.id
  /**
   * Returns true if the IBT is paid or voided or linked to a bill or has been invoiced
   * @returns {boolean}
   */
  const statusIsPaidOrVoided = () =>
    inboundTicketData?.status?.value === TICKET_STATUSES.PAID.id || isVoided() || ibtIsLinkedToBill() || ibtIsInvoiced()
  /**
   * Returns true if the IBT is voided or linked to a bill or has been invoiced
   * @returns {boolean}
   */
  const statusIsVoided = () => isVoided() || ibtIsLinkedToBill() || ibtIsInvoiced()
  const canPay = useCallback(
    () =>
      !hasDataConflict &&
      !statusIsPaidOrVoided() &&
      (inboundTicketData?.materials?.length > 0 || inboundTicketData?.scrapVehicles?.length > 0) &&
      (!isInLockedAccountingPeriodV1 ||
        inboundTicketData?.status?.value === TICKET_STATUSES.PRICE_COMPLETE.id ||
        inboundTicketData?.status?.value === TICKET_STATUSES.APPROVED.id),
    [inboundTicketData, hasDataConflict, isInLockedAccountingPeriodV1],
  )
  useEffect(() => {
    dispatch(clearCustomer())
    tabsContext.setContainerTabsVisibility(false)
  }, [])

  useEffect(() => {
    if (customerData) {
      customerDataRef.current = customerData
    }
  }, [inboundTicketData, customerData])

  useEffect(() => {
    supplierRef.current = supplier
    if (supplier && supplier.customerCommonIdentifierString) {
      const currentCustomer = getValues('customer.name')
      // Only mark as dirty if:
      // 1. It's a new ticket, OR
      // 2. There's already a customer selected AND the new customer has a different ID
      const hasCustomerChanged = currentCustomer?.id && currentCustomer.id !== supplier.id
      const shouldDirty = !inboundTicketId || hasCustomerChanged

      clearErrors('customer.name')
      setValue('customer.name', supplier, { shouldDirty })
      setValue('customer.nametext', supplier?.customerCommonIdentifierString || '', { shouldDirty })
    }
  }, [viewMode, supplier])

  useEffect(() => {
    if (
      customerData &&
      !selectedCustomer?.customerCommonIdentifierString &&
      customerData?.id === selectedCustomer?.id
    ) {
      dispatch(
        setCustomer({
          id: selectedCustomer?.id,
          customerCommonIdentifierString: customerData.customerCommonIdentifierString,
          supplierType: customerData.supplierType,
        }),
      )
    }
  }, [customerData])

  const purchaseOrderPriceSheetId =
    purchaseOrderData?.id === purchaseOrderId ? purchaseOrderData?.priceSheet?.id : undefined
  const priceListPriceSheetId = customerData?.id === selectedCustomer?.id ? customerData?.priceSheetId : undefined

  useEffect(() => {
    dispatch(setSupplementalPriceSheetIds([purchaseOrderPriceSheetId, priceListPriceSheetId].filter(id => !!id)))

    return () => clearSupplementalPriceSheetIds()
  }, [purchaseOrderPriceSheetId, priceListPriceSheetId])

  const onClickVoid = async () => {
    setShowVoidModal(false)
    if (isVoided()) {
      setErrorMessage(`Inbound ticket ${inboundTicketData?.ticketId} already VOID`)
      return null
    }
    await voidInboundTicket({
      ticketId: inboundTicketId,
    })
      .unwrap()
      .then(() => {
        trackEvent(cz.VOID_IBT)
        navigate('/app/purchases')
      })
      .catch(error => {
        const errorMsg = error?.data?.error || error?.error || error?.message || 'An error occurred voiding the ticket.'
        trackEvent({
          eventName: cz.VOID_IBT,
          errorMsg,
        })
        setErrorMessage(errorMsg)
      })
    return null
  }

  /**
   * Update vehicle info with customer
   */
  const updateVehicleWithCustomerAndIBT = async (vehicle, customerId, _inboundTicketId, ibtVehicleId) => {
    const isDefaultRetail = customerId === defaultRetailCustomer?.id
    if (vehicle?.id && !isDefaultRetail && (vehicle?.customerId !== customerId || vehicle?.id !== ibtVehicleId)) {
      const values = formatVehicleData({
        ...vehicle,
        customerId,
        inboundTicketId: _inboundTicketId,
      })
      if (values.registrationExpiration === null) {
        delete values.registrationExpiration
      }
      await updateVehicle({
        vehicleId: vehicle.id,
        vehicle: values,
      })
        .unwrap()
        .catch(error => {
          const errorMsg = error?.data?.error || error?.error || error?.message || 'An error occurred updating vehicle.'
          setErrorMessage(errorMsg)
        })
    }
    dispatch(clearNewCustomerVehicle())
  }

  /**
   * Checks if tare weight is required for materials and if it is missing.
   * @param {Array} materials - The array of materials to check.
   * @param {string} view - The view option for the inbound ticket.
   * @returns {boolean} - Returns true if tare weight is required for some materials and missing, otherwise false.
   */
  const errorsTareRequired = (materials, view) => {
    let tareRequiredErrors = []
    if (view === IBT_VIEW_OPTIONS.SCALE) {
      tareRequiredErrors = materials.filter(
        material => material.materialType?.tareWeightRequired && material?.weightAndPrice?.tare === null,
      )
    } else {
      tareRequiredErrors = materials.filter(row => row.tareWeightRequired && row.tare === null)
    }
    if (tareRequiredErrors.length > 0) {
      tareRequiredErrors.forEach(row =>
        setError(`materialGrid.${row.id}.tare`, { type: 'required', message: 'Tare is required' }),
      )
      setErrorMessage('Tare weight is required for some materials.')
      return true
    }
    return false
  }

  /**
   * Watch load information and push to ref.
   */

  useEffect(() => {
    loadInformationRef.current = loadInformation
  }, [loadInformation])

  useEffect(() => {
    localNotesRef.current = localNotes
  }, [localNotes])

  const updateTicketActionsAndStatusArguments = {
    contentContext,
    inboundTicketData,
    billData,
    control,
    hasEditedMaterials,
    isDirty,
    errors,
    setActiveDialog,
    handlePrint,
    setShowVoidModal,
    methods,
    canPay,
    navigate,
    isUpdate,
    inboundTicketId,
    canUseDefaultCustomer,
    defaultRetailCustomer,
    canUpdateStatus: !isTransfer && !statusIsPaidOrVoided() && !hasDataConflict && !isInLockedAccountingPeriodV1,
    enableTruckInYardIBTStatusFeatureFlag,
    enableMaterialPaymentHolds,
    enableNegativeIbtToInvoice,
    enableFePdfs,
    errorsTareRequired,
    isInLockedAccountingPeriodV1,
    handleOpenEmailModal,
    enablePdfEmails,
    loadInformation,
    currentTotal,
    dispatch,
  }

  const wrappedUpdateTicketActionsAndStatus = useCallback(
    (onSubmit, hasDataConflict, isAutoSaving = false, total = null) => {
      const totalToUse = total !== null ? total : currentTotal
      // Only update actions if onSubmit is provided (from Industrial container)
      if (onSubmit) {
        updateTicketActionsAndStatus({
          ...updateTicketActionsAndStatusArguments,
          onSubmit,
          hasDataConflict,
          isAutoSaving,
          currentTotal: totalToUse,
        })
      }
    },
    [...Object.values(updateTicketActionsAndStatusArguments), hasDataConflict],
  )

  const isAutosaveEnabled = enableIbtAutosave && isAutoSaveSettingEnabled && !hasDataConflict
  const onAddVehicle = (tab = 'scrap-vehicle') => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.ADD_VEHICLE,
        formModalProps: {
          tab,
        },
      }),
    )
  }

  const sharedProps = {
    purchaseOrderData,
    inboundTicketNotes: localNotes,
    inboundTicketNotesRef: localNotesRef,
    onSaveNotes: setLocalNotes,
    isUpdate,
    inboundTicketId,
    inboundTicketData,
    billData,
    customerData,
    carrierData,
    supplierRef,
    customerDataRef,
    createInboundTicket,
    updateInboundTicket,
    setErrorMessage,
    loadInformation,
    setLoadInformation,
    loadInformationRef,
    statusIsPaidOrVoided,
    statusIsVoided,
    freightBillIsPaid,
    isDirty,
    isCustomerInfoDirty,
    hasEditedMaterials,
    setHasEditedMaterials,
    wrappedUpdateTicketActionsAndStatus,
    isAutosaveEnabled,
    onAddVehicle,
    vehicleData: filteredVehicleData,
    onEditScrapVehicle: onScrapVehicleEdit,
    updateVehicleWithCustomerAndIBT,
    userIsInactive,
    isTransfer,
    pricePrepend,
    carrierPricePrepend,
    defaultRetailCustomer,
    hasDataConflict,
    ticketHashRef: ticketHash,
    errorsTareRequired,
    setIsFetchingMaterialPrices,
    isInLockedAccountingPeriodV1,
    enableFePdfs,
    handlePrint,
    purchaseOrderLocationId,
    feesAndPromotionsData,
    checkDoNotBuyViolation,
    onTotalUpdate: setCurrentTotal,
  }

  const getAddressFromContact = contact => ({
    address1: contact?.address1,
    address2: contact?.address2,
    city: contact?.city,
    state: contact?.state,
    zip: contact?.zip,
  })

  const paymentMethodsData = useMemo(
    () => (billData?.paymentMethods ? groupBy(billData.paymentMethods, pm => pm.paymentMethod) : {}),
    [billData],
  )

  const paymentMethodsOnPayments = paymentsData?.map(payment => ({ method: payment.paymentMethod }))

  const roundingDelta = useRoundingDelta(paymentMethodsOnPayments, paymentMethodsData)

  const printableInboundTicketData = useMemo(() => {
    let contactLocation = {}
    const primaryContact = customerData?.contacts?.find(contact => !!contact.isPrimaryContact)
    if (primaryContact?.address1) {
      contactLocation = getAddressFromContact(primaryContact)
    } else {
      const firstContact = customerData?.contacts?.find(contact => !!contact.address1)
      const validCustomerLocations = customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []

      if (firstContact) {
        contactLocation = getAddressFromContact(firstContact)
      } else if (validCustomerLocations.length > 0) {
        contactLocation = validCustomerLocations[0]
      }
    }

    const { scrapVehiclesAsMaterials } = formatScrapVehicles(inboundTicketData?.scrapVehicles)
    const inboundTicketMaterials = (inboundTicketData?.materials || []).map(material => {
      const purchaseOrderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(
        entry => entry.id === material.orderEntryId,
      )
      return {
        ...material,
        materialName: material?.materialType?.commonName,
        overrideName: purchaseOrderEntry?.overrideName,
      }
    })
    const materialsFormatted = [...inboundTicketMaterials, ...scrapVehiclesAsMaterials]

    return {
      ...inboundTicketData,
      netValue: Dinero(inboundTicketData?.netValue)?.toUnit(),
      taxRate: billTaxRate,
      materials: materialsFormatted,
      customer: {
        ...inboundTicketData?.customer,
        contactLocation,
      },
      carrierName: carrierData?.displayName,
      roundingDelta,
    }
  }, [inboundTicketData, customerData, billTaxRate, roundingDelta, purchaseOrderData])

  const handleOnAfterPrint = dialogAction => {
    if (dialogAction === PRINT_ACTION.PurchaseFull && isAutoSaveAndCloseAfterPrintingEnabled) {
      navigate('/app/purchases/ticket-library')
    }
    if (dialogAction === PRINT_ACTION.ScaleFull && isAutoSaveAndCloseAfterPrintingScaleTicketEnabled) {
      navigate('/app/purchases/ticket-library')
    }
  }

  useEffect(() => {
    if (!purchaseOrderId) {
      setPurchaseOrderLocationId(null)
      return
    }
    if (purchaseOrderId !== purchaseOrderData?.id) return
    if (purchaseOrderData?.supplierId !== customerData?.id) return
    setPurchaseOrderLocationId(purchaseOrderData?.purchaseOrderLocationId)
  }, [purchaseOrderId, purchaseOrderData, customerData])

  return (
    <FormProvider {...methods}>
      <input type="hidden" ref={ticketHash} />
      {activeDialog && imagesAreReady && ticketCreator && (
        <PrintDialog
          title={activeDialog.dialogTitle}
          open={!!activeDialog}
          onClose={() => setActiveDialog(null)}
          dialogType={activeDialog.dialogType}
          showLogoAddress
          onAfterPrint={() => handleOnAfterPrint(activeDialog.action)}
        >
          {renderActiveDialog({
            inboundTicketData: printableInboundTicketData,
            notes: localNotes?.map(note => {
              return { ...note, name: note.note }
            }),
            ticketCreator,
            activeDialog,
            paymentData: {
              paymentMethod: generatePaymentMethodText(paymentsData),
              checkNumber: generateCheckNumberText(paymentsData),
              signatureImageData,
              fingerprintImageData,
              paymentDate: paymentsData.sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate))[0]
                ?.paymentDate,
            },
            customerData,
            vehicleData: filteredVehicleData,
            pricePrepend,
          })}
        </PrintDialog>
      )}
      {showVoidModal && (
        <AlertModal
          open={showVoidModal}
          modalType="void"
          title="Void This Inbound Ticket"
          question="Are you sure you want to void this inbound ticket?"
          description={`Inbound ticket #${inboundTicketData?.ticketId} will be voided and all data will be lost.`}
          confirmLabel="YES, CONFIRM VOID"
          cancelLabel="GO BACK"
          onClose={() => {
            setShowVoidModal(false)
          }}
          onSubmit={onClickVoid}
        />
      )}
      <EmailModal
        open={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        recipients={customerData?.contacts?.map(contact => contact.email).filter(Boolean)}
        pdfTypeOptions={[PDF_TYPE.IBT_SCALE_TICKET, PDF_TYPE.IBT_PURCHASE_TICKET]}
        defaultPdfType={PDF_TYPE.IBT_SCALE_TICKET}
        entityId={inboundTicketId}
      />
      <RouterPrompt
        title="Cancel Changes to This Ticket"
        question="Are you sure you want to cancel changes to this ticket?"
        description="Changes to the ticket will be cancelled and all unsaved data on the ticket will be lost."
        when={!isClearingReduxFormState && (isDirty || hasEditedMaterials) && !submittedFromDispatchBlade}
        onSubmitAction={() => {
          if (inboundTicketData) {
            const {
              transportationInfo: { purchaseOrderNumber: poNumber, purchaseOrderId: poId },
              customer,
            } = inboundTicketData
            dispatch(
              setPurchaseOrderInfo({
                purchaseOrderNumber: poNumber?.toString() || '',
                purchaseOrderId: poId || '',
              }),
            )
            dispatch(setCustomer(customer))
          }
          dispatch(clearNewCustomerVehicle())
        }}
      />
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />
      {hasDataConflict && (
        <Alert severity="error">
          The data for this ticket has been updated by another user. Please refresh the page to see the latest data.
        </Alert>
      )}
      {viewMode === IBT_VIEW_OPTIONS.SCALE ? (
        <InboundTicketScaleContainer {...sharedProps} />
      ) : (
        <InboundTicketIndustrialContainer {...sharedProps} />
      )}
    </FormProvider>
  )
}

export default InboundTicketContainer
